export const PROVIDERS = [
	{ key: "openai", resultKey: "openai_result", name: "ChatGPT" },
	{ key: "gemini", resultKey: "gemini_result", name: "Google Gemini" },
	{ key: "claude", resultKey: "claude_result", name: "Claude AI" },
];

const QUERY_TYPE_LABELS = {
	informational: "Informational",
	comparison: "Comparison",
	best_of: "Best Of",
	product_recommendation: "Branded",
	ingredient_or_how: "Branded",
	availability: "Branded",
	custom: "Custom",
	unknown: "Query",
};

const QUERY_TYPE_CLASS = {
	informational: "query-type--informational",
	comparison: "query-type--comparison",
	best_of: "query-type--best_of",
	product_recommendation: "query-type--branded",
	ingredient_or_how: "query-type--branded",
	availability: "query-type--branded",
	custom: "query-type--custom",
	unknown: "query-type--unknown",
};

export function getQueryTypeClass(type) {
	return QUERY_TYPE_CLASS[type] || QUERY_TYPE_CLASS.unknown;
}

export function getQueryTypeLabel(type) {
	return QUERY_TYPE_LABELS[type] || "Query";
}

export function preprocessResponse(text) {
	if (!text) return "";
	let processed = text.replace(/\*\*\s*(https?:\/\/[^\s*]+)\s*\*\*/g, "[$1]($1)");
	processed = processed.replace(/(?<!\]\()https?:\/\/[^\s<>)]+/g, (url) => {
		const cleaned = url.replace(/[)\]*,;:.!?]+$/, "");
		return `[${cleaned}](${cleaned})`;
	});
	return processed;
}

export function getProviderResults(result) {
	return PROVIDERS.map((p) => result[p.resultKey]).filter(Boolean);
}

export function getProviderStatusBadge(result) {
	if (!result) return null;
	if (result.cited) return { variant: "success", label: "Cited" };
	if (result.mentioned) return { variant: "info", label: "Mentioned" };
	return { variant: "secondary", label: "Not cited" };
}

export function queryMatchesFilter(result, filter) {
	const providers = getProviderResults(result);
	if (filter === "mentioned") return providers.some((p) => p.mentioned);
	if (filter === "cited") return providers.some((p) => p.cited);
	if (filter === "domain") return providers.some((p) => p.domain_mentioned);
	if (filter === "brand") return providers.some((p) => p.brand_mentioned);
	return true;
}

export function sourceToDomain(source) {
	try {
		return new URL(source).hostname.replace(/^www\./, "");
	} catch {
		return String(source).replace(/^https?:\/\/(www\.)?/, "").split("/")[0];
	}
}

export function formatGroundedSource(source) {
	return `[Grounded: ${sourceToDomain(source)}]`;
}

export function getRowSources(row) {
	if (Array.isArray(row.sources) && row.sources.length > 0) {
		return row.sources;
	}
	const combined = [...(row.competitor_sources || []), ...(row.own_sources || [])];
	if (combined.length > 0) {
		return [...new Set(combined)];
	}
	return [];
}

export function getRowSourceCount(row) {
	if (row.total_sources != null && row.total_sources > 0) {
		return row.total_sources;
	}
	return getRowSources(row).length;
}

export function customPromptToGridRow(prompt, mode, hasWebSearch) {
	const queryType = prompt.queryType || prompt.query_type || "custom";

	if (mode === "websearch" && hasWebSearch && prompt.websearch_results) {
		const ws = prompt.websearch_results;
		return {
			query: prompt.question,
			query_type: queryType,
			openai_result: ws.openai ?? ws.openai_result ?? undefined,
			gemini_result: ws.gemini ?? ws.gemini_result ?? undefined,
			claude_result: ws.claude ?? ws.claude_result ?? undefined,
		};
	}

	return {
		query: prompt.question,
		query_type: queryType,
		openai_result: prompt.openai_result ?? undefined,
		gemini_result: prompt.gemini_result ?? undefined,
		claude_result: prompt.claude_result ?? undefined,
	};
}

export function buildCitationGridResults({
	queries,
	queryTypes,
	perProvider,
	customPrompts,
	mode,
	hasWebSearch,
}) {
	const baseRows = queries.map((query) => ({
		query,
		query_type: queryTypes?.[query] || "unknown",
		openai_result: perProvider.openai?.results?.find((r) => r.query === query),
		gemini_result: perProvider.gemini?.results?.find((r) => r.query === query),
		claude_result: perProvider.claude?.results?.find((r) => r.query === query),
	}));

	const querySet = new Set(queries);
	const customRows = (customPrompts || [])
		.filter((p) => p?.question && !querySet.has(p.question))
		.filter((p) => mode !== "websearch" || !hasWebSearch || p.websearch_results)
		.map((p) => customPromptToGridRow(p, mode, hasWebSearch));

	return [...baseRows, ...customRows];
}
